import React, {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useToast} from "../../modules/layout/components/ToastContext";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import Loader from "../../modules/layout/components/Loader";
import {Field, Form, Formik} from "formik";
import FiltersCard from "../../modules/layout/components/FiltersCard";
import {FormikDatePicker} from "../../modules/layout/components/FormikDatePicker";
import {getAll} from "../_request";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DatePicker from "react-datepicker";
import {Typeahead} from "react-bootstrap-typeahead";
import Pagination from "../../modules/layout/components/Pagination";
import {useAuth} from "../../../setup/auth/authService";
import {
    IConsumptionDashboard,
    IConsumptionDashboardTopRow,
    IConsumptionReportRow,
    IFilterConsumptionReportModel
} from "../../interfaces/IUtilities";

const initialFilters: IFilterConsumptionReportModel = {
    from: '',
    to: '',
    spokeName: '',
    className: []
};

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const buildQuery = (filters: IFilterConsumptionReportModel) => {
    const parts: string[] = [];
    if (filters.from) parts.push(`from=${encodeURIComponent(formatDate(new Date(filters.from)))}`);
    if (filters.to)   parts.push(`to=${encodeURIComponent(formatDate(new Date(filters.to)))}`);
    if (filters.spokeName) parts.push(`spokeName=${encodeURIComponent(filters.spokeName)}`);
    if (filters.className && filters.className.length > 0) parts.push(`className=${encodeURIComponent(filters.className.join(','))}`);
    return parts.length ? '?' + parts.join('&') : '';
};


const HEADER_STYLES = {
    fillColor: [0, 0, 0] as [number, number, number],
    textColor: [255, 255, 255] as [number, number, number],
    fontStyle: 'bold' as const
};

const drawSegments = (
    doc: jsPDF,
    segments: { text: string; bold: boolean }[],
    startX: number,
    startY: number,
    maxWidth: number,
    lineHeight = 5
): number => {
    type Token = { text: string; bold: boolean; isSpace: boolean };
    const tokens: Token[] = [];
    segments.forEach(seg => {
        seg.text.split(/(\s+)/).forEach(p => {
            if (!p) return;
            tokens.push({ text: p, bold: seg.bold, isSpace: /^\s+$/.test(p) });
        });
    });

    let x = startX;
    let y = startY;
    let pendingSpace = false;

    tokens.forEach(tok => {
        if (tok.isSpace) {
            pendingSpace = true;
            return;
        }
        doc.setFont('helvetica', tok.bold ? 'bold' : 'normal');
        const needsSpace = pendingSpace && x > startX;
        const drawText = needsSpace ? ' ' + tok.text : tok.text;
        const drawWidth = doc.getTextWidth(drawText);

        if (x + drawWidth > startX + maxWidth && x > startX) {
            y += lineHeight;
            x = startX;
            const bareWidth = doc.getTextWidth(tok.text);
            doc.text(tok.text, x, y);
            x += bareWidth;
        } else {
            doc.text(drawText, x, y);
            x += drawWidth;
        }
        pendingSpace = false;
    });

    return y;
};

const generatePdf = (
    t: (k: string, opts?: any) => string,
    filters: IFilterConsumptionReportModel,
    dashboard: IConsumptionDashboard | null,
    perSpoke: IConsumptionReportRow[],
    perService: IConsumptionReportRow[],
    generatedBy: string
) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 15;

    const monthsShort = t('MonthsShort').split('|');
    const fmtShortDate = (d: Date) => `${String(d.getDate()).padStart(2, '0')} ${monthsShort[d.getMonth()]} ${d.getFullYear()}`;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text(t('ConsumptionReports'), 14, y);
    y += 7;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    const generatedLine = generatedBy
        ? `${t('GeneratedAt')}: ${fmtShortDate(new Date())}  ${t('GeneratedBy')} ${generatedBy}`
        : `${t('GeneratedAt')}: ${fmtShortDate(new Date())}`;
    doc.text(generatedLine, 14, y);
    y += 6;

    doc.setTextColor(0);
    doc.setFontSize(10);

    const spokeStr = filters.spokeName?.trim() ?? '';
    const classArr = (filters.className && filters.className.length > 0) ? filters.className : [];
    const fromDate = filters.from ? new Date(filters.from) : null;
    const toDate = filters.to ? new Date(filters.to) : null;
    const periodStr = fromDate && toDate ? `${fmtShortDate(fromDate)} - ${fmtShortDate(toDate)}` : '';

    if (spokeStr || classArr.length > 0 || periodStr) {
        const segments: { text: string; bold: boolean }[] = [];

        segments.push({ text: `${t('ReportIntro')} `, bold: false });

        if (spokeStr && classArr.length > 0) {
            segments.push({ text: `${t('ForUser')} `, bold: false });
            segments.push({ text: spokeStr, bold: true });
            segments.push({ text: ` ${t('WithTransactions')} `, bold: false });
            segments.push({ text: classArr.join(', '), bold: true });
        } else if (spokeStr) {
            segments.push({ text: `${t('ForUser')} `, bold: false });
            segments.push({ text: spokeStr, bold: true });
        } else if (classArr.length > 0) {
            segments.push({ text: `${t('ForTransactions')} `, bold: false });
            segments.push({ text: classArr.join(', '), bold: true });
        }

        if (periodStr) {
            segments.push({ text: ` ${t('DuringPeriod')} `, bold: false });
            segments.push({ text: periodStr, bold: true });
        }
        segments.push({ text: '.', bold: false });

        y = drawSegments(doc, segments, 14, y, pageWidth - 28) + 8;
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    if (dashboard) {
        const isFullMonth = (() => {
            if (!filters.from || !filters.to) return true;
            const f = new Date(filters.from);
            const to = new Date(filters.to);
            if (f.getFullYear() !== to.getFullYear() || f.getMonth() !== to.getMonth()) return false;
            if (f.getDate() !== 1) return false;
            const lastDay = new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate();
            return to.getDate() === lastDay;
        })();
        const periodLabel = isFullMonth ? t('CallsThisMonth') : t('CallsThisPeriod');
        const todayIso = new Date().toISOString().slice(0, 10);
        const yestIso = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); })();
        const latest = dashboard.latestDay ? dashboard.latestDay.slice(0, 10) : null;
        const callsPdfLabel = latest === todayIso ? t('CallsToday')
                            : latest === yestIso ? t('CallsYesterday')
                            : latest ? `${t('CallsOn')} ${latest}`
                            : t('CallsToday');
        const avgPdfLabel   = latest === todayIso ? t('AvgResponseMsToday')
                            : latest === yestIso ? t('AvgResponseMsYesterday')
                            : latest ? `${t('AvgResponseMsOn')} ${latest}`
                            : t('AvgResponseMsToday');
        autoTable(doc, {
            startY: y,
            head: [[t('Metric'), t('Value')]],
            body: [
                [callsPdfLabel, String(dashboard.callsToday)],
                [periodLabel, String(dashboard.callsThisMonth)],
                [t('SuccessRate'), `${(dashboard.successRateThisMonth * 100).toFixed(1)}%`],
                [avgPdfLabel, dashboard.avgResponseMsToday != null ? `${dashboard.avgResponseMsToday} ms` : '—']
            ],
            theme: 'grid',
            styles: { fontSize: 9 },
            headStyles: HEADER_STYLES
        });
        y = ((doc as any).lastAutoTable.finalY ?? y) + 6;
    }

    // Hide Top Spokes when a specific spoke is filtered (it would only contain that one).
    if (dashboard && dashboard.topSpokes.length > 0 && !spokeStr) {
        autoTable(doc, {
            startY: y,
            head: [[t('TopSpokes'), t('TotalCalls')]],
            body: dashboard.topSpokes.map(r => [r.displayName || r.key, String(r.totalCalls)]),
            theme: 'striped',
            styles: { fontSize: 9 },
            headStyles: HEADER_STYLES
        });
        y = ((doc as any).lastAutoTable.finalY ?? y) + 6;
    }

    // Hide Top Classes when a single class is filtered (redundant).
    if (dashboard && dashboard.topClasses.length > 0 && classArr.length !== 1) {
        autoTable(doc, {
            startY: y,
            head: [[t('TopClasses'), t('TotalCalls')]],
            body: dashboard.topClasses.map(r => [r.key, String(r.totalCalls)]),
            theme: 'striped',
            styles: { fontSize: 9 },
            headStyles: HEADER_STYLES
        });
        y = ((doc as any).lastAutoTable.finalY ?? y) + 6;
    }

    const spokeFiltered = !!spokeStr;
    if (perSpoke.length > 0) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(t('PerSpokeReport'), 14, y);
        y += 4;
        const head = spokeFiltered
            ? [[t('ClassName'), t('TotalCalls'), t('SuccessfulCalls'), t('Errors'), t('AvgResponseMs')]]
            : [[t('SpokeName'), t('ClassName'), t('TotalCalls'), t('SuccessfulCalls'), t('Errors'), t('AvgResponseMs')]];
        const body = perSpoke.map(r => {
            const base = [
                r.className,
                String(r.totalCalls),
                String(r.successfulCalls),
                String(r.totalCalls - r.successfulCalls),
                r.avgResponseMs != null ? String(r.avgResponseMs) : '—'
            ];
            return spokeFiltered ? base : [r.spokeName || r.spokeUserId, ...base];
        });
        autoTable(doc, {
            startY: y,
            head,
            body,
            theme: 'grid',
            styles: { fontSize: 8 },
            headStyles: HEADER_STYLES
        });
        y = ((doc as any).lastAutoTable.finalY ?? y) + 6;
    }

    if (perService.length > 0) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(t('PerServiceReport'), 14, y);
        y += 4;
        autoTable(doc, {
            startY: y,
            head: [[t('ClassName'), t('TotalCalls'), t('SuccessfulCalls'),
                    t('Errors'), t('AvgResponseMs')]],
            body: perService.map(r => [
                r.className,
                String(r.totalCalls),
                String(r.successfulCalls),
                String(r.totalCalls - r.successfulCalls),
                r.avgResponseMs != null ? String(r.avgResponseMs) : '—'
            ]),
            theme: 'grid',
            styles: { fontSize: 8 },
            headStyles: HEADER_STYLES
        });
    }

    const fname = `consumption-report-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')}.pdf`;
    doc.save(fname);
};

const ConsumptionReports = () => {
    const {t} = useTranslation();
    const {showToast} = useToast();
    const {user} = useAuth();
    const generatedBy = (user?.profile?.preferred_username as string | undefined)
        || user?.profile?.name
        || `${user?.profile?.given_name || ''} ${user?.profile?.family_name || ''}`.trim()
        || '';

    const [activeFilters, setActiveFilters] = useState<IFilterConsumptionReportModel>(initialFilters);
    const [dashboard, setDashboard] = useState<IConsumptionDashboard | null>(null);
    const [perSpoke, setPerSpoke] = useState<IConsumptionReportRow[]>([]);
    const [perService, setPerService] = useState<IConsumptionReportRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [classNameOptions, setClassNameOptions] = useState<string[]>([]);

    useEffect(() => {
        const fetchClassNames = async () => {
            try {
                const res = await getAll('/api/consumptionlogs/classnames', '');
                if (Array.isArray(res?.data)) {
                    setClassNameOptions(res.data as string[]);
                }
            } catch { /* silent — filter still usable via free typing */ }
        };
        fetchClassNames();
    }, []);

    const loadAll = useCallback(async (filters: IFilterConsumptionReportModel) => {
        try {
            setLoading(true);

            const [dashRes, perSpokeRes, perServiceRes] = await Promise.all([
                getAll('/api/consumptionlogs/dashboard', buildQuery(filters)),
                getAll('/api/consumptionlogs/reports/per-spoke', buildQuery(filters)),
                getAll('/api/consumptionlogs/reports/per-service', buildQuery(filters))
            ]);

            const dash = dashRes?.data && typeof dashRes.data === 'object' && !Array.isArray(dashRes.data)
                ? dashRes.data as IConsumptionDashboard
                : null;
            if (dash) {
                dash.topSpokes = Array.isArray(dash.topSpokes) ? dash.topSpokes : [];
                dash.topClasses = Array.isArray(dash.topClasses) ? dash.topClasses : [];
            }
            setDashboard(dash);

            setPerSpoke(Array.isArray(perSpokeRes?.data) ? perSpokeRes.data as IConsumptionReportRow[] : []);
            setPerService(Array.isArray(perServiceRes?.data) ? perServiceRes.data as IConsumptionReportRow[] : []);
        } catch (error) {
            showToast(t('ConnectionError', {error: String(error)}), 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast, t]);

    useEffect(() => {
        loadAll(initialFilters);
    }, [loadAll]);

    const handleSearch = async (filters: IFilterConsumptionReportModel) => {
        setActiveFilters(filters);
        await loadAll(filters);
    };

    const formatPercent = (v: number) => `${(v * 100).toFixed(1)}%`;

    return (
        <div className="gg-consumptionReports container mt-4">
            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('ConsumptionReports')}
                ]}/>

            <h3 className="mb-3">{t('ConsumptionReports')}</h3>

            <FiltersCard>
                <Formik initialValues={activeFilters} onSubmit={handleSearch} enableReinitialize>
                    {({resetForm, values, setFieldValue}) => {
                        const today = new Date();
                        const fromVal = values.from ? new Date(values.from) : null;
                        const toVal = values.to ? new Date(values.to) : null;
                        const monthVal = fromVal && toVal
                            && fromVal.getFullYear() === toVal.getFullYear()
                            && fromVal.getMonth() === toVal.getMonth()
                            && fromVal.getDate() === 1
                            && toVal.getDate() === new Date(toVal.getFullYear(), toVal.getMonth() + 1, 0).getDate()
                            ? fromVal : null;
                        return (
                        <Form>
                            <div className="row">
                                <div className="col-md-6 col-lg-3">
                                    <label className="form-label w-100">{t('Month')}
                                        <DatePicker
                                            className="form-control"
                                            popperClassName="gg-datepicker-popper"
                                            popperPlacement="bottom-start"
                                            selected={monthVal}
                                            onChange={(d: Date | null) => {
                                                if (!d) {
                                                    setFieldValue('from', '');
                                                    setFieldValue('to', '');
                                                    return;
                                                }
                                                const first = new Date(d.getFullYear(), d.getMonth(), 1);
                                                const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
                                                const maxDay = last > today ? today : last;
                                                setFieldValue('from', first);
                                                setFieldValue('to', maxDay);
                                            }}
                                            showMonthYearPicker
                                            dateFormat="yyyy-MM"
                                            placeholderText={t('ChooseMonth')}
                                            maxDate={today}
                                            isClearable
                                            autoComplete="off"
                                        />
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-3">
                                    <label className="form-label w-100">{t('DateFrom')}
                                        <FormikDatePicker name="from" placeholderText={t('ChooseDate')}
                                                          maxDate={toVal && toVal < today ? toVal : today}/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-3">
                                    <label className="form-label w-100">{t('DateTo')}
                                        <FormikDatePicker name="to" placeholderText={t('ChooseDate')}
                                                          minDate={fromVal}
                                                          maxDate={today}/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-3">
                                    <label className="form-label w-100">{t('SpokeName')}
                                        <Field className="form-control" name="spokeName"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-3">
                                    <label className="form-label w-100">{t('ClassName')}
                                        <Typeahead
                                            id="reports-className-typeahead"
                                            multiple
                                            allowNew
                                            minLength={1}
                                            newSelectionPrefix={t('AddClass') + ': '}
                                            selected={Array.isArray(values.className) ? values.className : []}
                                            options={classNameOptions}
                                            placeholder={t('SearchClass')}
                                            onChange={(sel) => {
                                                const asStrings = sel.map((s: any) =>
                                                    typeof s === 'string' ? s : (s.label ?? s.customOption ?? ''));
                                                setFieldValue('className', asStrings.filter((x: string) => !!x));
                                            }}
                                        />
                                    </label>
                                </div>
                                <div className="col-12 align-self-end">
                                    <button type="submit" className="btn gg-btn gg-btn-submit mb-2 me-2">
                                        {t('Search')}
                                    </button>
                                    <button type="button" className="btn btn-secondary text-white mb-2 me-2"
                                            onClick={() => {
                                                resetForm();
                                                setActiveFilters(initialFilters);
                                                handleSearch(initialFilters);
                                            }}>
                                        {t('Reset')}
                                    </button>
                                    <button type="button" className="btn btn-outline-dark mb-2"
                                            disabled={loading || (!dashboard && perSpoke.length === 0 && perService.length === 0)}
                                            onClick={() => generatePdf(t, activeFilters, dashboard, perSpoke, perService, generatedBy)}>
                                        {t('DownloadPdf')}
                                    </button>
                                </div>
                            </div>
                        </Form>
                        );
                    }}
                </Formik>
            </FiltersCard>

            {loading && <Loader/>}

            {dashboard && (() => {
                const isFullMonth = (() => {
                    if (!activeFilters.from || !activeFilters.to) return true;
                    const f = new Date(activeFilters.from);
                    const to = new Date(activeFilters.to);
                    if (f.getFullYear() !== to.getFullYear() || f.getMonth() !== to.getMonth()) return false;
                    if (f.getDate() !== 1) return false;
                    const lastDay = new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate();
                    return to.getDate() === lastDay;
                })();
                const periodLabel = isFullMonth ? t('CallsThisMonth') : t('CallsThisPeriod');
                const todayIso = new Date().toISOString().slice(0, 10);
                const yesterdayIso = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); })();
                const latest = dashboard.latestDay ? dashboard.latestDay.slice(0, 10) : null;
                const isToday = latest === todayIso;
                const isYesterday = latest === yesterdayIso;
                const callsLabel = isToday ? t('CallsToday')
                                 : isYesterday ? t('CallsYesterday')
                                 : latest ? `${t('CallsOn')} ${latest}`
                                 : t('CallsToday');
                const avgLabel = isToday ? t('AvgResponseMsToday')
                               : isYesterday ? t('AvgResponseMsYesterday')
                               : latest ? `${t('AvgResponseMsOn')} ${latest}`
                               : t('AvgResponseMsToday');
                return (
                <div className="row mb-3 gg-kpis mt-3">
                    <KpiTile label={callsLabel} value={dashboard.callsToday} variant="info"/>
                    <KpiTile label={periodLabel} value={dashboard.callsThisMonth} variant="primary"/>
                    <KpiTile label={t('SuccessRate')}
                             value={formatPercent(dashboard.successRateThisMonth)}
                             variant={dashboard.successRateThisMonth >= 0.95 ? "success"
                                    : dashboard.successRateThisMonth >= 0.80 ? "warning" : "danger"}/>
                    <KpiTile label={avgLabel}
                             value={dashboard.avgResponseMsToday ?? '—'}
                             suffix={dashboard.avgResponseMsToday != null ? 'ms' : undefined}
                             variant="secondary"/>
                </div>
                );
            })()}

            {dashboard && (
                <div className="row mb-3">
                    <div className="col-md-6 mb-2">
                        <TopList title={t('TopSpokes')} rows={dashboard.topSpokes} useDisplayName/>
                    </div>
                    <div className="col-md-6 mb-2">
                        <TopList title={t('TopClasses')} rows={dashboard.topClasses}/>
                    </div>
                </div>
            )}

            <h5 className="mt-4">{t('PerSpokeReport')}</h5>
            <ReportTable rows={perSpoke} showSpoke={true}/>

            <h5 className="mt-4">{t('PerServiceReport')}</h5>
            <ReportTable rows={perService} showSpoke={false}/>
        </div>
    );
};

const KpiTile: React.FC<{
    label: string;
    value: React.ReactNode;
    suffix?: string;
    variant: 'info' | 'primary' | 'success' | 'warning' | 'danger' | 'secondary';
}> = ({label, value, suffix, variant}) => (
    <div className="col-md-3 col-sm-6 mb-2">
        <div className={`card gg-kpi-card gg-kpi-${variant} shadow-sm border-0 h-100`}>
            <div className="card-body py-3">
                <div className="text-uppercase small fw-semibold gg-kpi-label mb-1">{label}</div>
                <div className="gg-kpi-value">
                    <span className="display-6 fw-bold">{value}</span>
                    {suffix && <span className="ms-1 text-muted small">{suffix}</span>}
                </div>
            </div>
        </div>
    </div>
);

const TOP_LIST_COLORS = [
    '#0d6efd', // blue
    '#198754', // green
    '#fd7e14', // orange
    '#6f42c1', // purple
    '#dc3545', // red
    '#20c997', // teal
    '#d63384', // pink
    '#ffc107', // amber
    '#0dcaf0', // cyan
    '#6610f2', // indigo
];

const hashKey = (s: string) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return Math.abs(h);
};

const TopList: React.FC<{
    title: string;
    rows: IConsumptionDashboardTopRow[];
    useDisplayName?: boolean;
}> = ({title, rows, useDisplayName}) => {
    const safe = Array.isArray(rows) ? rows : [];
    const max = safe.reduce((m, r) => Math.max(m, r.totalCalls), 0);
    return (
        <div className="card gg-top-list shadow-sm border-0 h-100">
            <div className="card-body">
                <h6 className="text-uppercase small fw-semibold text-muted mb-3">{title}</h6>
                {safe.length === 0 && <div className="text-muted small">&mdash;</div>}
                {safe.map(r => {
                    const display = useDisplayName ? (r.displayName || r.key) : r.key;
                    const pct = max > 0 ? Math.round((r.totalCalls / max) * 100) : 0;
                    const color = TOP_LIST_COLORS[hashKey(r.key) % TOP_LIST_COLORS.length];
                    return (
                        <div key={r.key} className="mb-2">
                            <div className="d-flex justify-content-between small mb-1">
                                <span className="text-truncate me-2"
                                      title={useDisplayName ? r.key : undefined}
                                      style={{maxWidth: '75%'}}>{display}</span>
                                <span className="fw-bold">{r.totalCalls}</span>
                            </div>
                            <div className="progress" style={{height: '6px'}}>
                                <div className="progress-bar"
                                     role="progressbar"
                                     style={{width: `${pct}%`, backgroundColor: color}}
                                     aria-valuenow={pct}
                                     aria-valuemin={0}
                                     aria-valuemax={100}/>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ReportTable: React.FC<{rows: IConsumptionReportRow[]; showSpoke: boolean}> = ({rows, showSpoke}) => {
    const {t} = useTranslation();
    const safe = Array.isArray(rows) ? rows : [];
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [safe.length]);

    const totalPages = Math.max(1, Math.ceil(safe.length / pageSize));
    const page = Math.min(currentPage, totalPages);
    const start = (page - 1) * pageSize;
    const paged = safe.slice(start, start + pageSize);

    return (
        <>
            <div className="table-responsive border p-1 rounded-2">
                <table className="table table-sm table-striped-columns small table-logs">
                    <thead>
                    <tr>
                        {showSpoke && <th>{t('SpokeName')}</th>}
                        <th>{t('ClassName')}</th>
                        <th>{t('TotalCalls')}</th>
                        <th>{t('SuccessfulCalls')}</th>
                        <th>{t('Errors')}</th>
                        <th>{t('AvgResponseMs')}</th>
                    </tr>
                    </thead>
                    <tbody className="table-group-divider">
                    {safe.length === 0 && (
                        <tr><td colSpan={showSpoke ? 6 : 5} className="text-muted">&mdash;</td></tr>
                    )}
                    {paged.map((r, idx) => (
                        <tr key={(showSpoke ? r.spokeUserId : '') + '_' + r.className + '_' + (start + idx)}>
                            {showSpoke && (
                                <td className="text-nowrap" title={r.spokeUserId}>
                                    {r.spokeName || r.spokeUserId}
                                </td>
                            )}
                            <td className="text-nowrap">{r.className}</td>
                            <td>{r.totalCalls}</td>
                            <td>{r.successfulCalls}</td>
                            <td>{r.totalCalls - r.successfulCalls}</td>
                            <td>{r.avgResponseMs ?? '—'}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            {safe.length > 0 && (
                <Pagination
                    currentPage={page}
                    onPageChange={setCurrentPage}
                    totalItems={safe.length}
                    pageSize={pageSize}
                    onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                />
            )}
        </>
    );
};

export default ConsumptionReports;
